import { Button } from "@/components/ui/button"
import { Form, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuth, useAuthStatus } from "@/store"
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { PasswordInput } from "./password-input"
import { getReturnUrl, withReturnUrl } from "@/utils/return-url"
import { withInvitationId } from "@/utils/invitation-url"

// Validation schema for login form
const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character")
})

type LoginFormData = z.infer<typeof loginSchema>

export interface LoginFormProps {
  onSuccess?: () => void
  prefilledEmail?: string
}

/**
 * Login form component with email and password validation
 * Features real-time validation, loading states, and integration with auth context
 */
/** Extracts slug from hostname (e.g., "sso" from "sso.taqatbot.com"). */
function getSlugFromHostname(): string | null {
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  // Need at least 3 parts: slug.domain.tld
  if (parts.length < 3) return null
  // Skip localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') return null
  return parts[0]
}

/**
 * Resolution state of the workspace SSO lookup. `loading` is distinct from `none`
 * so the form never guesses a login method before the config request resolves.
 */
type SsoState =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'optional'; workspaceName: string; slug: string }
  | { status: 'required'; workspaceName: string; slug: string }

/** Neutral placeholder: card chrome only, hinting at neither login method. */
function LoginFormSkeleton() {
  return (
    <div className="w-full" data-testid="login-form-skeleton" aria-busy="true">
      <div className="text-left mb-6 space-y-2">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  )
}

export function LoginForm({ onSuccess, prefilledEmail }: LoginFormProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const invitationId = searchParams.get('invitationId')
  const { login, user } = useAuth()
  const { isLoading, error, clearError } = useAuthStatus()
  /** Workspace whose SSO to offer when the host carries no tenant subdomain. */
  const ssoSlugParam = searchParams.get('ssoSlug')
  const [sso, setSso] = useState<SsoState>(() =>
    ssoSlugParam || getSlugFromHostname() ? { status: 'loading' } : { status: 'none' }
  )

  // Check if SSO is available for this domain
  useEffect(() => {
    // An explicit slug wins over the hostname, which is only ever a guess:
    // `getSlugFromHostname` treats the first label of ANY three-part host as a
    // tenant (a tunnel or a marketing subdomain included), so on those hosts
    // the guess is wrong and would mask the caller's real intent. The param is
    // how a flow that already knows the workspace — a channel "link your
    // account" card, which always points at the canonical host — keeps SSO
    // reachable. Naming a slug reveals nothing: /api/auth/sso/config is
    // public, and /authorize only proceeds for a workspace that has SSO.
    const slug = ssoSlugParam ?? getSlugFromHostname()
    if (!slug) return

    const controller = new AbortController()

    fetch(`/api/auth/sso/config?slug=${slug}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((json: { success: boolean; data?: { enabled: boolean; provider: string; workspaceName: string; enforcement: string } }) => {
        if (controller.signal.aborted) return
        if (json.success && json.data?.enabled) {
          setSso({
            status: json.data.enforcement === 'required' ? 'required' : 'optional',
            workspaceName: json.data.workspaceName,
            slug,
          })
        } else {
          setSso({ status: 'none' })
        }
      })
      .catch(() => {
        // A workspace with no SSO config 404s, so a failed lookup resolves to
        // "no SSO" rather than leaving the form stuck on the placeholder.
        if (!controller.signal.aborted) setSso({ status: 'none' })
      })

    return () => controller.abort()
  }, [ssoSlugParam])

  /** Redirects to the SSO authorization endpoint. */
  const handleSsoLogin = () => {
    if (sso.status !== 'optional' && sso.status !== 'required') return
    // Carry the post-login destination through the IdP round trip. Without it
    // the callback drops the user on /app and whatever they were trying to
    // reach — an invitation, a channel link — is silently abandoned.
    const returnUrl = getReturnUrl(searchParams) ?? (
      invitationId ? withInvitationId('/app', invitationId) : null
    )
    const returnParam = returnUrl
      ? `&redirect_uri=${encodeURIComponent(`${window.location.origin}${returnUrl}`)}`
      : ''
    window.location.href = `/api/auth/sso/authorize?slug=${encodeURIComponent(sso.slug)}${returnParam}`
  }

  // Clear any previous auth errors when component mounts
  // This prevents showing stale errors from session restoration failures
  useEffect(() => {
    clearError()
  }, [clearError])

  const form = useForm<LoginFormData>({
    resolver: safeZodResolver(loginSchema as any),
    defaultValues: {
      email: prefilledEmail || "",
      password: "",
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    // Clear any previous error
    clearError()
    const result = await login(data.email, data.password, invitationId ?? undefined) as { redirectUrl?: string, user?: any } | null

    if (!result) return

    // If there's a redirect URL (e.g., from invitation), navigate there
    if (result?.redirectUrl) {
      const redirectUrl = result.redirectUrl.startsWith('/')
        ? result.redirectUrl
        : `/${result.redirectUrl}`
      navigate(withInvitationId(redirectUrl, invitationId))
      return
    }

    // Show success toast with username
    // @ts-ignore
    const username = result?.user?.name || user?.name || data.email
    toast.success(`Welcome back ${username}`)

    // Call success callback to handle navigation
    onSuccess?.()
  }

  if (sso.status === 'loading') {
    return <LoginFormSkeleton />
  }

  return (
    <div className="w-full">
      {sso.status === 'required' ? (
        <>
          <div className="text-left mb-6">
            <h1 className="text-2xl font-semibold tracking-tight">
              Sign in to {sso.workspaceName}
            </h1>
            <p className="text-sm text-muted-foreground">
              Your organization requires SSO login
            </p>
          </div>

          <div className="space-y-4">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12"
              onClick={handleSsoLogin}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              This workspace requires sign-in through your organization&apos;s identity provider.
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="text-left mb-6">
            <h1 className="text-2xl font-semibold tracking-tight">
              Sign in to your account
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your email and password to access your account
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onFormValidationError)} className="space-y-4">
              <FormItem>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  {...form.register("email")}
                  disabled={isLoading}
                />
                <FormMessage>
                  {form.formState.errors.email?.message}
                </FormMessage>
              </FormItem>

              <FormItem>
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  placeholder="Enter your password"
                  {...form.register("password")}
                  disabled={isLoading}
                />
                <FormMessage>
                  {form.formState.errors.password?.message}
                </FormMessage>
              </FormItem>

              {error && (
                <FormMessage>
                  {error}
                </FormMessage>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>

              {sso.status === 'optional' && (
                <>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-3 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleSsoLogin}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                    </svg>
                    Sign in with Microsoft
                  </Button>
                </>
              )}

              <div className="space-y-2 text-center text-sm">
                <Link
                  to="/forgot-password"
                  className="text-primary hover:text-primary/80 underline-offset-4 hover:underline"
                >
                  Forgot your password?
                </Link>
                <p className="text-muted-foreground">
                  Don't have an account?{" "}
                  <Link
                    to={withReturnUrl("/signup", searchParams)}
                    className="text-primary hover:text-primary/80 underline-offset-4 hover:underline"
                  >
                    Sign up
                  </Link>
                </p>
              </div>
            </form>
          </Form>
        </>
      )}
    </div>
  )
}
